import { ContactMedium } from '../../generated/api/contactMedium.js';
import { BillingAccountDeliveryMethod } from '../../generated/api/models.js';
import type { Address } from '../../generated/api/address.js';
import type { BillingAccountCoco } from '../../generated/api/billingAccountCoco.js';
import type { BillingAccountsResponse } from '../../generated/api/billingAccountsResponse.js';
import type { RelatedPartyRef } from '../../generated/api/relatedPartyRef.js';

export enum BillingAccountType {
  SFDC = 'SFDC',
  CONSUMER_BA = 'Consumer BA',
}

export enum BillingAccountRelatedPartyRole {
  PAYER = 'payer',
  OWNER = 'owner',
  CONTACT = 'Contact',
  BILL_RECEIVER = 'billReceiver',
}

export const BillingAccountLanguageCode = {
  FINNISH: 'FI',
  SWEDISH: 'SV',
  ENGLISH: 'EN',
} as const;

export const DEFAULT_BILLING_ACCOUNT_LANGUAGE = BillingAccountLanguageCode.FINNISH;

const ELECTRONIC_OPERATOR_ID_CHARACTERISTIC = 'eOperatorId';
const ELECTRONIC_OPERATOR_NAME_CHARACTERISTIC = 'eOperatorName';
const ELECTRONIC_INVOICE_ADDRESS_CHARACTERISTIC = 'eInvoiceAddress';
const BUSINESS_ID_IDENTIFICATION_TYPE = 'businessId';

export const getBillingAccountPayer = (billingAccount: BillingAccountCoco): RelatedPartyRef | undefined =>
  billingAccount.relatedParty?.find(party => party.role === BillingAccountRelatedPartyRole.PAYER);

export const getBillingAccountPayerName = (billingAccount: BillingAccountCoco): string =>
  getBillingAccountPayer(billingAccount)?.engagedParty?.name ?? '';

export const getBillingAccountLabelCoCo = ({ abpAccountNumber, name }: BillingAccountCoco): string =>
  [abpAccountNumber, name].filter(Boolean).join(' ');

export const getBillingAccountPayerBusinessId = (billingAccount: BillingAccountCoco): string =>
  getBillingAccountPayer(billingAccount)?.engagedParty?.organizationIdentification?.identificationType ===
  BUSINESS_ID_IDENTIFICATION_TYPE
    ? (getBillingAccountPayer(billingAccount)?.engagedParty?.organizationIdentification?.identificationId ?? '')
    : '';

export const getBillingAccountPayerAddress = (billingAccount: BillingAccountCoco): Address => {
  const postalMedium = getBillingAccountPayer(billingAccount)?.engagedParty?.contactMedium?.find(
    medium => medium.mediumType === ContactMedium.MediumTypeEnum.postalAddress
  );
  return {
    line1: postalMedium?.characteristic?.street1 ?? '',
    postalCode: postalMedium?.characteristic?.postCode ?? '',
    postOffice: postalMedium?.characteristic?.city ?? '',
    countryCode: postalMedium?.characteristic?.country ?? '',
  };
};

export const getBillingAccountDeliveryMethod = (billingAccount: BillingAccountCoco): BillingAccountDeliveryMethod => {
  switch (billingAccount.billDeliveryMethod?.[0]?.id) {
    case BillingAccountDeliveryMethod.EMAIL:
      return BillingAccountDeliveryMethod.EMAIL;
    case BillingAccountDeliveryMethod.PAPER:
      return BillingAccountDeliveryMethod.PAPER;
    case BillingAccountDeliveryMethod.ELECTRONIC:
      return BillingAccountDeliveryMethod.ELECTRONIC;
    default:
      return BillingAccountDeliveryMethod.UNKNOWN;
  }
};

export const getBillingAccountBillReceiverName = (billingAccount: BillingAccountCoco): string =>
  billingAccount.relatedParty?.find(party => party.role === BillingAccountRelatedPartyRole.BILL_RECEIVER)?.engagedParty
    ?.name ?? '';

export interface BillingAccountElectronicDetails {
  operator: string;
  address: string;
}

export const getBillingAccountElectronicDetails = (
  billingAccount: BillingAccountCoco
): BillingAccountElectronicDetails => {
  const characteristics = billingAccount.billDeliveryMethod?.[0]?.characteristic ?? [];
  const getCharacteristicValue = (name: string): string =>
    characteristics.find(characteristic => characteristic.name === name)?.value ?? '';

  return {
    operator:
      getCharacteristicValue(ELECTRONIC_OPERATOR_NAME_CHARACTERISTIC) ||
      getCharacteristicValue(ELECTRONIC_OPERATOR_ID_CHARACTERISTIC),
    address: getCharacteristicValue(ELECTRONIC_INVOICE_ADDRESS_CHARACTERISTIC),
  };
};

// Maps CoCo billing accounts to the shape of the billing account search, used by the shared billing account components
export const toBillingAccountsResponseCoCo = (billingAccounts: BillingAccountCoco[]): BillingAccountsResponse => ({
  total: billingAccounts.length,
  searchResults: billingAccounts.map(billingAccount => {
    const deliveryMethod = getBillingAccountDeliveryMethod(billingAccount);
    return {
      result: {
        billingAccountId: billingAccount.id,
        billingAccountDisplayId: billingAccount.abpAccountNumber,
        billingAccountName: billingAccount.name,
        payerName: getBillingAccountPayerName(billingAccount),
        billReceiverName: getBillingAccountBillReceiverName(billingAccount),
        deliveryMethod: deliveryMethod === BillingAccountDeliveryMethod.UNKNOWN ? undefined : deliveryMethod,
      },
    };
  }),
});
